import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiGrid,
  FiTrendingUp,
  FiDollarSign,
  FiSettings,
  FiLogOut,
  FiInfo,
  FiAlertTriangle,
  FiMessageCircle,
} from "react-icons/fi";
import { FaRobot } from "react-icons/fa6";
import { getApiBase } from "../apiBase";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
  Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import HeaderActions from "../components/HeaderActions";
import Sidebar from "../components/Sidebar";
import { FaSignLanguage } from "react-icons/fa";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  LineController,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
);

/* ==========================================
   DESIGN TOKENS (same theme, flat + professional)
   Plum:  #3D2542 (ink)  #5B3A60 (brand)  #F0E6F5 (tint)  #FAF8FB (page bg)
   Gold:  #B89D4F (accent)  #A88D3F (accent-dark)
   Mauve: #8B5D75 (tertiary series accent, same family)
   Line:  #E4DCE6
   Text:  #7A6B7E (muted)
   State: success #1F9D6C / #E8F7F0   error #C62828 / #FDEEEE
   ========================================== */

const StatCard = ({ label, value, accent = "plum" }) => {
  const accents = {
    plum: "border-l-[#5B3A60]",
    gold: "border-l-[#B89D4F]",
    mauve: "border-l-[#8B5D75]",
  };
  return (
    <div className={`bg-white p-5 rounded-lg border border-[#E4DCE6] border-l-4 ${accents[accent]}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-[#7A6B7E] mb-2">{label}</p>
      <div className="text-2xl font-bold text-[#3D2542] break-words">{value}</div>
    </div>
  );
};

export default function Analytics() {
  const formatDate = (date) => date.toISOString().split("T")[0];

  const getDefaultRange = () => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    return {
      from: formatDate(from),
      to: formatDate(to),
    };
  };

  const navigate = useNavigate();
  const location = useLocation();
  const tenantId = localStorage.getItem("tenant_id");
  const storedCompanyName = localStorage.getItem("company_name");
  const [activeBotsOnly, setActiveBotsOnly] = useState(true);
  const [appliedPeriod, setAppliedPeriod] = useState("day");
  const [appliedActiveBotsOnly, setAppliedActiveBotsOnly] = useState(true);

  const companyName =
    storedCompanyName && storedCompanyName !== "undefined" && storedCompanyName !== "null" ? storedCompanyName : "My Workspace";

  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [period, setPeriod] = useState("day");

  const defaultRange = getDefaultRange();
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);
  const [appliedRange, setAppliedRange] = useState(defaultRange);

  const [sidebarOpen, setSidebarOpen] = useState(true);

  const fetchAnalytics = async () => {
    if (!tenantId || !appliedRange) return;
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const url = `${getApiBase()}/analytics/tenant/${tenantId}/tenant-level-analytics??period=${appliedPeriod}&start_date=${appliedRange.from}&end_date=${appliedRange.to}&active_bots_only=${appliedActiveBotsOnly}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setAnalyticsData(data);
    } catch (error) {
      setAnalyticsError(error.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (!storedCompanyName && tenantId) {
      fetch(`${getApiBase()}/tenant/${tenantId}/overview`)
        .then((res) => res.json())
        .then((data) => {
          if (data.company_name) {
            localStorage.setItem("company_name", data.company_name);
          }
        });
    }
  }, [tenantId]);

  useEffect(() => {
    fetchAnalytics();
  }, [tenantId, appliedRange, appliedPeriod, appliedActiveBotsOnly]);

  const computeTotalUsers = () => {
    const active = analyticsData?.summary?.usage_activity?.active_users;
    const drop = analyticsData?.summary?.usage_activity?.dropoff_rate_percent;
    if (!active || drop == null) return "-";
    const rate = drop / 100;
    if (rate >= 1) return "-";
    return Math.round(active / (1 - rate));
  };

  const formatSeconds = (value) => {
    if (value == null) return "-";
    return `${Number(value).toFixed(2)} s`;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#3D2542",
        padding: 12,
        cornerRadius: 6,
        titleFont: { size: 12, weight: "600" },
        bodyFont: { size: 12 },
        displayColors: true,
        boxPadding: 4,
        callbacks: {
          label: (ctx) => {
            const label = ctx.dataset.label || "";
            const value = ctx.raw;
            if (label.includes("Latency")) return `${label}: ${value.toFixed(2)} s`;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false, drawBorder: false },
        ticks: { color: "#7A6B7E", font: { size: 11 } },
      },
      yLatency: {
        type: "linear",
        position: "left",
        beginAtZero: true,
        title: { display: true, text: "Latency (s)", color: "#7A6B7E", font: { size: 11, weight: "600" } },
        ticks: { color: "#A89CA5", font: { size: 10 }, callback: (value) => value.toFixed(2) },
        grid: { color: "#F0E6F5", drawBorder: false },
      },
      yUsage: {
        type: "linear",
        position: "right",
        beginAtZero: true,
        title: { display: true, text: "Volume", color: "#7A6B7E", font: { size: 11, weight: "600" } },
        ticks: { color: "#A89CA5", font: { size: 10 } },
        grid: { display: false, drawBorder: false },
      },
    },
  };

  const chartData = {
    labels: analyticsData?.time_series_data?.map((d) => d.period) || [],
    datasets: [
      {
        type: "line",
        label: "Avg Latency",
        data: analyticsData?.time_series_data?.map((d) => d.avg_latency_ms) || [],
        borderColor: "#8B5D75",
        borderWidth: 2,
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#8B5D75",
        pointBorderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.35,
        yAxisID: "yLatency",
        fill: false,
      },
      {
        type: "bar",
        label: "Sessions",
        data: analyticsData?.time_series_data?.map((d) => d.total_sessions) || [],
        backgroundColor: "#EFDFAF",
        borderRadius: 3,
        barPercentage: 0.55,
        yAxisID: "yUsage",
      },
      {
        type: "bar",
        label: "Messages",
        data: analyticsData?.time_series_data?.map((d) => d.total_messages) || [],
        backgroundColor: "#5B3A60",
        borderRadius: 3,
        barPercentage: 0.55,
        yAxisID: "yUsage",
      },
    ],
  };

  if (analyticsLoading) {
    return (
      <div className="flex min-h-screen bg-[#FAF8FB] text-[#3D2542] font-['Poppins'] overflow-hidden">
        <Sidebar tenantId={tenantId} companyName={companyName} sidebarOpen={sidebarOpen} userRole={localStorage.getItem("role") || "member"}
 />
        <main className="flex-1 min-h-screen flex items-center justify-center" style={{ marginLeft: sidebarOpen ? 240 : 72 }}>
          <div className="flex flex-col items-center gap-4">
            <motion.div
              className="w-10 h-10 rounded-full border-[3px] border-[#B89D4F] border-t-transparent"
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 0.8, ease: "linear" }}
            />
            <p className="text-[#7A6B7E] font-medium text-sm">Loading analytics…</p>
          </div>
        </main>
      </div>
    );
  }

  if (analyticsError) {
    return (
      <div className="flex min-h-screen bg-[#FAF8FB] text-[#3D2542] font-['Poppins'] overflow-hidden">
        <Sidebar tenantId={tenantId} companyName={companyName} sidebarOpen={sidebarOpen} userRole={localStorage.getItem("role") || "member"}
/>
        <main className="flex-1 min-h-screen flex items-center justify-center p-6" style={{ marginLeft: sidebarOpen ? 240 : 72 }}>
          <div className="bg-white p-8 rounded-lg border border-[#F0C7C7] max-w-md w-full text-center">
            <div className="w-14 h-14 bg-[#FDEEEE] text-[#C62828] rounded-full flex items-center justify-center mx-auto mb-4">
              <FiAlertTriangle size={24} />
            </div>
            <h3 className="text-lg font-bold text-[#3D2542] mb-2">Unable to Load Analytics</h3>
            <p className="text-[#7A6B7E] text-sm mb-6">{analyticsError}</p>
            <button onClick={fetchAnalytics} className="px-6 py-2.5 bg-[#5B3A60] hover:bg-[#4A2E50] text-white rounded-md font-semibold text-sm transition-colors w-full">
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#FAF8FB] text-[#3D2542] font-['Poppins'] overflow-hidden">
      <Sidebar tenantId={tenantId} companyName={companyName} sidebarOpen={sidebarOpen} userRole={localStorage.getItem("role") || "member"}
/>

      <main className="flex-1 min-h-screen transition-all duration-200 overflow-x-hidden" style={{ marginLeft: sidebarOpen ? 240 : 72 }}>
        <header className="sticky top-0 z-30 bg-white border-b border-[#E4DCE6] px-8 h-16 flex items-center">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-md hover:bg-[#F0E6F5] transition-colors">
                {sidebarOpen ? <FiChevronLeft className="text-[#7A6B7E]" size={18} /> : <FiChevronRight className="text-[#7A6B7E]" size={18} />}
              </button>
              <div className="flex items-center gap-1.5 text-sm">
                <span className="font-medium text-[#7A6B7E]">{companyName || "Workspace"}</span>
                <span className="text-[#C9BDCC]">/</span>
                <span className="font-semibold text-[#3D2542]">Analytics</span>
              </div>
            </div>
            <HeaderActions tenantId={tenantId} fullName={localStorage.getItem("fullname")} />
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="mb-7">
            <h1 className="text-xl font-bold text-[#3D2542]">Analytics</h1>
            <p className="text-sm text-[#7A6B7E] mt-1">Track usage, performance, and engagement across your workspace.</p>
          </div>

          <div className="space-y-6">
            {/* FILTER BAR */}
            <div className="bg-white p-5 rounded-lg border border-[#E4DCE6] flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#7A6B7E] mb-1.5 ml-0.5">From</label>
                  <input
                    type="date"
                    max={formatDate(new Date())}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="px-3 py-2 text-sm bg-white border border-[#E4DCE6] rounded-md outline-none focus:ring-2 focus:ring-[#5B3A60]/10 focus:border-[#5B3A60] text-[#3D2542] font-medium"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider font-semibold text-[#7A6B7E] mb-1.5 ml-0.5">To</label>
                  <input
                    type="date"
                    max={formatDate(new Date())}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="px-3 py-2 text-sm bg-white border border-[#E4DCE6] rounded-md outline-none focus:ring-2 focus:ring-[#5B3A60]/10 focus:border-[#5B3A60] text-[#3D2542] font-medium"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex bg-[#FAF8FB] p-1 rounded-md border border-[#E4DCE6]">
                  {["day", "month"].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      className={`px-4 py-1.5 text-xs font-semibold rounded transition-colors ${
                        period === p ? "bg-white text-[#3D2542] border border-[#E4DCE6]" : "text-[#7A6B7E] hover:text-[#3D2542]"
                      }`}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2.5 bg-[#FAF8FB] px-3 py-2 rounded-md border border-[#E4DCE6]">
                  <span className="text-xs font-medium text-[#7A6B7E]">Active Bots Only</span>
                  <button
                    onClick={() => setActiveBotsOnly(!activeBotsOnly)}
                    className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${activeBotsOnly ? "bg-[#5B3A60]" : "bg-[#D7CBDA]"}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                        activeBotsOnly ? "translate-x-4" : ""
                      }`}
                    />
                  </button>
                </div>
                <button
                  onClick={() => {
                    if (!fromDate || !toDate) return;
                    setAppliedRange({ from: fromDate, to: toDate });
                    setAppliedPeriod(period);
                    setAppliedActiveBotsOnly(activeBotsOnly);
                  }}
                  disabled={!fromDate || !toDate}
                  className="px-5 py-2 bg-[#5B3A60] hover:bg-[#4A2E50] disabled:opacity-50 text-white text-sm font-semibold rounded-md transition-colors whitespace-nowrap"
                >
                  Apply
                </button>
                <button
                  onClick={() => {
                    const def = getDefaultRange();
                    setFromDate(def.from);
                    setToDate(def.to);
                    setAppliedRange(def);
                  }}
                  className="px-4 py-2 bg-white border border-[#E4DCE6] text-[#7A6B7E] hover:border-[#5B3A60] hover:text-[#3D2542] text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* STAT ROW */}
            {analyticsData && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                <StatCard label="Total Sessions" value={analyticsData.summary.usage_activity.total_sessions.toLocaleString()} accent="plum" />
                <StatCard label="Total Messages" value={analyticsData.summary.usage_activity.total_messages.toLocaleString()} accent="gold" />
                <StatCard label="Avg Latency" value={formatSeconds(analyticsData.summary.performance_quality.avg_latency_ms)} accent="mauve" />
                <StatCard label="P95 Latency" value={formatSeconds(analyticsData.summary.performance_quality.p95_latency_ms)} accent="mauve" />
                <StatCard label="Escalation Rate" value={`${analyticsData.summary.escalations.escalation_rate_percent}%`} accent="plum" />
              </div>
            )}

            {/* TREND CHART */}
            {analyticsData && analyticsData.time_series_data.length > 0 ? (
              <div className="bg-white p-6 rounded-lg border border-[#E4DCE6]">
                <div className="mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <h3 className="text-[15px] font-bold text-[#3D2542]">Usage & Performance Trends</h3>
                  <div className="flex gap-4 flex-wrap">
                    <span className="flex items-center text-xs text-[#7A6B7E]">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#5B3A60] mr-2" />
                      Messages
                    </span>
                    <span className="flex items-center text-xs text-[#7A6B7E]">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#EFDFAF] mr-2" />
                      Sessions
                    </span>
                    <span className="flex items-center text-xs text-[#7A6B7E]">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#8B5D75] mr-2" />
                      Latency
                    </span>
                  </div>
                </div>
                <div className="h-[380px] w-full">
                  <Chart data={chartData} options={chartOptions} />
                </div>
              </div>
            ) : (
              <div className="bg-white p-12 rounded-lg border border-dashed border-[#E4DCE6] text-center">
                <div className="w-14 h-14 bg-[#F0E6F5] rounded-full flex items-center justify-center mx-auto mb-4 text-[#5B3A60]">
                  <FiTrendingUp size={22} />
                </div>
                <h3 className="text-[15px] font-bold text-[#3D2542]">No Data Available</h3>
                <p className="text-[#7A6B7E] text-sm mt-1">Try adjusting the date range to see analytics.</p>
              </div>
            )}

            {/* BREAKDOWN GRID */}
            {analyticsData && (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {[
                  {
                    title: "Performance & Cost",
                    subtitle: "How fast the bot responds and operational costs",
                    icon: <FiTrendingUp className="text-[#5B3A60]" size={16} />,
                    items: [
                      { label: "Avg Latency", value: formatSeconds(analyticsData.summary.performance_quality.avg_latency_ms) },
                      { label: "P95 Latency", value: formatSeconds(analyticsData.summary.performance_quality.p95_latency_ms) },
                    ],
                  },
                  {
                    title: "User Engagement",
                    subtitle: "How actively users interact with the bot",
                    icon: <FaSignLanguage className="text-[#8B5D75]" size={16} />,
                    items: [
                      { label: "Total Users (Est)", value: computeTotalUsers() },
                      { label: "Active Users", value: analyticsData.summary.usage_activity.active_users.toLocaleString() },
                      { label: "Avg Msg / Session", value: analyticsData.summary.usage_activity.avg_messages_per_session },
                      { label: "Dropoff Rate", value: `${analyticsData.summary.usage_activity.dropoff_rate_percent}%` },
                    ],
                  },
                  {
                    title: "User Feedback",
                    subtitle: "How users rate the bot's quality",
                    icon: <FiMessageCircle className="text-[#B89D4F]" size={16} />,
                    items: [
                      { label: "Thumbs Up", value: analyticsData.summary.user_feedback.thumbs_up },
                      { label: "Thumbs Down", value: analyticsData.summary.user_feedback.thumbs_down },
                      {
                        label: "Avg Rating",
                        value: analyticsData.summary.user_feedback.avg_star_rating ? `${analyticsData.summary.user_feedback.avg_star_rating} / 5` : "N/A",
                      },
                    ],
                  },
                  {
                    title: "Automation Health",
                    subtitle: "Bot's ability to handle conversations autonomously",
                    icon: <FiSettings className="text-[#7A6B7E]" size={16} />,
                    items: [
                      { label: "Total Escalations", value: analyticsData.summary.escalations.total_escalations },
                      { label: "Escalation Rate", value: `${analyticsData.summary.escalations.escalation_rate_percent}%` },
                    ],
                  },
                ].map((block, i) => (
                  <div key={i} className="bg-white rounded-lg border border-[#E4DCE6] overflow-hidden">
                    <div className="px-6 py-4 border-b border-[#E4DCE6] bg-[#FAF8FB] flex items-center gap-3">
                      <div className="w-9 h-9 bg-white rounded-md border border-[#E4DCE6] flex items-center justify-center shrink-0">{block.icon}</div>
                      <div>
                        <h4 className="font-bold text-[#3D2542] text-sm">{block.title}</h4>
                        <p className="text-[11px] text-[#7A6B7E]">{block.subtitle}</p>
                      </div>
                    </div>
                    <div className="p-6">
                      <ul className="space-y-3">
                        {block.items.map((stat, idx) => (
                          <li key={idx} className="flex justify-between items-center">
                            <span className="text-sm text-[#7A6B7E] font-medium">{stat.label}</span>
                            <span className="text-sm font-bold text-[#3D2542] bg-[#FAF8FB] px-3 py-1.5 rounded-md border border-[#E4DCE6] whitespace-nowrap">
                              {stat.value}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* BOT TABLE */}
            {analyticsData?.summary?.bots_overview?.bot_details?.length > 0 && (
              <div className="bg-white rounded-lg border border-[#E4DCE6]">
                <div className="px-6 py-4 border-b border-[#E4DCE6] flex items-center gap-2.5">
                  <FaRobot className="text-[#B89D4F]" size={16} />
                  <h3 className="text-[15px] font-bold text-[#3D2542]">Bot Usage Breakdown</h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-[#FAF8FB]">
                      <tr>
                        <th className="px-6 py-3 text-left text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Bot Name</th>
                        <th className="px-6 py-3 text-center text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-center text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Sessions</th>
                        <th className="px-6 py-3 text-center text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Messages</th>
                        <th className="px-6 py-3 text-center text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider">Storage (MB)</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-[#E4DCE6]">
                      {analyticsData.summary.bots_overview.bot_details.map((bot) => (
                        <tr key={bot.bot_id} className="hover:bg-[#FAF8FB] transition-colors">
                          <td className="px-6 py-3.5 text-left font-semibold text-[#3D2542]">{bot.bot_name}</td>
                          <td className="px-6 py-3.5 text-center">
                            <span
                              className={`px-2.5 py-0.5 rounded text-xs font-semibold border ${
                                bot.status === "deleted"
                                  ? "bg-[#FDEEEE] text-[#C62828] border-[#F0C7C7]"
                                  : "bg-[#E8F7F0] text-[#1F9D6C] border-[#BFE8D5]"
                              }`}
                            >
                              {bot.status}
                            </span>
                          </td>
                          <td className="px-6 py-3.5 text-center font-semibold text-[#3D2542]">{bot.total_sessions}</td>
                          <td className="px-6 py-3.5 text-center font-semibold text-[#3D2542]">{bot.total_messages}</td>
                          <td className="px-6 py-3.5 text-center font-semibold text-[#3D2542]">{bot.storage_mb}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}